import type { AuditLogDto, ListAuditLogsResponse, OwnerEntity } from '@bhops/shared';
import { apiClient } from './client';

export interface ListAuditLogsQuery {
  /** Exact match, or prefix with `.*` suffix (e.g. `privacy-request.*`). */
  action?: string;
  resourceType?: string;
  resourceId?: string;
  /**
   * 로봇 번호로 그 로봇의 기록만 보기 — `#113` · `113` · 긴 시리얼 일부.
   * 오너 지적(2026-08-31): `resourceId` 는 내부 id 정확 일치라 번호로는 0건이었다.
   */
  robotSerial?: string;
  actorUserId?: string;
  ownerEntity?: OwnerEntity;
  outcome?: 'success' | 'failure';
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * 체인 검증 결과 — 서버 `common/audit/chain.ts` 의 VerifyChainResult 와 같은 모양.
 *
 * 세 수를 **따로** 센다. 하나로 합치면 "확인 불가"가 "통과"로 둔갑한다:
 *   · verified        — 내용까지 재계산해 일치를 확인한 행
 *   · skipped         — 해시가 아예 없던 시기(체인 도입 전)의 행
 *   · legacyDiffRows  — 날짜가 해시에 덮이지 않던 시기의 행. **확인 불가**다
 *                       (연결은 검증되지만 내용은 재계산할 수 없다).
 */
export interface VerifyChainResult {
  verified: number;
  skipped: number;
  legacyDiffRows: number;
  latestLegacyDiffAt: string | null;
  issues: Array<{
    rowId: string;
    kind: 'prevHash-mismatch' | 'rowHash-mismatch';
    expected: string;
    actual: string;
    occurredAt: string;
  }>;
}

export const auditLogsApi = {
  async list(query: ListAuditLogsQuery = {}): Promise<ListAuditLogsResponse> {
    const { data } = await apiClient.get<ListAuditLogsResponse>('/audit-logs', {
      params: query,
    });
    return data;
  },

  async get(id: string): Promise<AuditLogDto> {
    const { data } = await apiClient.get<AuditLogDto>(`/audit-logs/${id}`);
    return data;
  },

  /**
   * 감사 보강 ①-D(2026-08-28) — 장부 자체의 위·변조 검증(관리자 전용).
   * 서버가 모든 행의 해시를 다시 계산해 저장된 값과 대조한다.
   */
  async verifyChain(): Promise<VerifyChainResult> {
    const { data } = await apiClient.get<VerifyChainResult>('/audit-logs/verify-chain');
    return data;
  },
};
